const IMAGE_EXTENSION = /\.(png|jpe?g|webp|gif)$/i

export const IMAGE_UNDERSTANDING_SUCCESS =
  "An image answer counts only when the uploaded picture stays visible, the reply describes that picture, and displayed values are quoted as shown. Unreadable or missing values must be called out instead of guessed."

export const IMAGE_TEST_SCENARIOS = [
  "Upload a tracking-page PNG and ask what the page shows. The picture stays on your message, and the reply names the screen and quotes a visible status or number.",
  "Ask for a value that is not on the image. The reply says it is not visible and does not invent it.",
  "Ask about the image and a SharePoint document together. The reply separates what is on the picture from any document it cites.",
  "A reply that lists numbers without saying they are on the image is marked unverified.",
]

export function isImageAttachment(file: { name: string; type?: string }) {
  if ((file.type || "").toLowerCase().startsWith("image/")) return true
  return IMAGE_EXTENSION.test(file.name)
}

export function attachmentContentType(file: { name: string; type?: string }) {
  const type = (file.type || "").toLowerCase()
  if (type.startsWith("image/")) return type
  if (/\.png$/i.test(file.name)) return "image/png"
  if (/\.jpe?g$/i.test(file.name)) return "image/jpeg"
  if (/\.webp$/i.test(file.name)) return "image/webp"
  if (/\.gif$/i.test(file.name)) return "image/gif"
  return type || "application/octet-stream"
}

export function imageGroundingPrompt(file: { name: string; type?: string }) {
  if (!isImageAttachment(file)) return ""
  return [
    `The user attached an image named ${file.name}. Answer from that image only.`,
    "Name the screen or page and the UI elements you can see.",
    "Quote displayed values exactly as they appear. Do not round, translate, or invent numbers, labels, or statuses.",
    "If a value is unreadable or not on the image, say it is not visible.",
    "Do not use SharePoint documents for this question.",
  ].join("\n")
}

const ATTRIBUTION =
  /\b(image|screenshot|picture|page|screen|shows|showing|displays|displayed|labeled|label|reads|visible|seen)\b/i
const CANNOT_SEE =
  /\b(can(?:not|'t)|unable to|could not)\b[\s\S]{0,40}\b(see|view|read|open)\b[\s\S]{0,40}\b(image|screenshot|picture|file|attachment)\b/i
const HAS_VALUE = /\b\d[\d,./:-]{1,}\b/

const COPILOT_IMAGE_REFUSAL = /not sure how to help with that/i
const FIELD_LABEL = /^(status|tracking number|carrier|destination|origin|eta|delivery date|ship date|page title)$/i
const BOILERPLATE = [
  /^the uploaded image\b/i,
  /^visible labels and values\b/i,
  /^if a requested value is not\b/i,
  /^these values are shown on the image\.?$/i,
]

function unquote(value: string) {
  return value.replace(/^["'`]+|["'`]+$/g, "").replace(/\*+/g, "").trim()
}

function isFieldLabel(value: string) {
  const text = unquote(value)
  if (FIELD_LABEL.test(text)) return true
  return text.length < 32 && /\b(number|date|status|carrier|destination|origin)\b/i.test(text) && !/\d{5,}/.test(text)
}

export function formatImageReading(fileName: string, reading: string) {
  const prose: string[] = []
  const items: string[] = []
  reading.replace(/\r\n/g, "\n").split("\n").map((line) => line.trim()).filter(Boolean).forEach((line) => {
    if (BOILERPLATE.some((pattern) => pattern.test(line))) return
    const bullet = line.match(/^(?:[-*•]|\d+\.)\s+(.*)$/)
    if (bullet) {
      items.push(unquote(bullet[1]))
      return
    }
    const plain = unquote(line)
    if (plain) prose.push(plain)
  })

  const fields: { label: string; value: string }[] = []
  let title = ""
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]
    const labeled = item.match(/^([^:]{2,40}):\s+(.+)$/)
    if (labeled && isFieldLabel(labeled[1])) {
      fields.push({ label: labeled[1].trim(), value: unquote(labeled[2]) })
    } else if (isFieldLabel(item) && items[index + 1] && !isFieldLabel(items[index + 1])) {
      fields.push({ label: item, value: items[index + 1] })
      index += 1
    } else if (!title) {
      title = item
    } else {
      fields.push({ label: "", value: item })
    }
  }

  const described = prose.find((line) => /\b(page|screen|shows|image)\b/i.test(line))
  const intro = described || (title ? `This page shows ${title}.` : `The image ${fileName} shows the following.`)
  const titleLine = title && intro.toLowerCase().indexOf(title.toLowerCase()) === -1 ? `**${title}**` : ""
  const rows = [
    ...fields.filter((field) => field.label).map((field) => `- **${field.label}:** ${field.value}`),
    ...fields.filter((field) => !field.label).map((field) => `- ${field.value}`),
  ]
  const missing = /\bnot visible\b/i.test(reading)
    ? "A value you asked for is not visible on the image."
    : ""
  return [intro, "These values are shown on the image.", titleLine, rows.join("\n"), missing]
    .filter(Boolean)
    .join("\n\n")
}

export function presentImageAnswer(text: string) {
  if (!/quoted as shown on the image|Visible labels and values exactly/i.test(text)) return text
  const fileName = (text.match(/uploaded image\s+(\S+)/i) || [])[1] || "image"
  const reading = text
    .replace(/^The uploaded image[\s\S]*?quoted as shown on the image\.\s*/i, "")
    .replace(/\n*If a requested value is not in that reading[\s\S]*$/i, "")
    .trim()
  return formatImageReading(fileName, reading)
}

export function imageAnswerFromReading(fileName: string, reading: string) {
  const body = reading.trim()
  if (!body) return ""
  return formatImageReading(fileName, body)
}

export function shouldUseImageReading(answer: string, fileName: string) {
  if (COPILOT_IMAGE_REFUSAL.test(answer)) return true
  return !reviewImageAnswer({ fileName, answer }).grounded
}

export function reviewImageAnswer(input: { fileName: string; answer: string }) {
  const answer = input.answer.trim()
  if (!answer) {
    return {
      grounded: false,
      summary: `The reply does not describe the uploaded image. ${IMAGE_UNDERSTANDING_SUCCESS}`,
    }
  }
  if (CANNOT_SEE.test(answer)) {
    return {
      grounded: false,
      summary: `The reply says the image was not read. ${IMAGE_UNDERSTANDING_SUCCESS}`,
    }
  }
  const attributesImage = ATTRIBUTION.test(answer) || answer.toLowerCase().includes(input.fileName.toLowerCase())
  const statesValues = HAS_VALUE.test(answer)
  if (!attributesImage) {
    return {
      grounded: false,
      summary: `The reply is not tied to the uploaded image. ${IMAGE_UNDERSTANDING_SUCCESS}`,
    }
  }
  if (statesValues && !/\b(shows|showing|displays|displayed|labeled|reads|visible|on the (image|screen|page|screenshot))\b/i.test(answer)) {
    return {
      grounded: false,
      summary: `Values are not quoted as shown on the image. ${IMAGE_UNDERSTANDING_SUCCESS}`,
    }
  }
  if (answer.length < 80) {
    return {
      grounded: false,
      summary: `The reply does not describe the image in enough detail. ${IMAGE_UNDERSTANDING_SUCCESS}`,
    }
  }
  return {
    grounded: true,
    summary: `Read from ${input.fileName}.`,
  }
}
